const SIZE_HEADINGS = new Set(['size details', 'afmetingen', '尺寸信息']);
const DESCRIPTION_SELECTOR = '.product-details .rte, .product-details [data-product-description], .product-details .text-block';
const LOWER_SELECTOR = '.cardc-dimensions[data-cardc-dimensions]';
const observedRoots = new WeakSet();
const observedMainContents = new WeakMap();

export function normalizeText(value) {
  return String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/[：:]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase();
}

export function isSizeDetailsHeading(value) {
  return SIZE_HEADINGS.has(normalizeText(value).replace(/&/g, '').replace(/\s+/g, ' ').trim());
}

export function canonicalDimensionLabel(value) {
  const label = normalizeText(value);
  return new Map([
    ['folded card', 'folded'],
    ['folded card size', 'folded'],
    ['gevouwen kaart', 'folded'],
    ['折叠尺寸', 'folded'],
    ['open display', 'open'],
    ['open display size', 'open'],
    ['open weergave', 'open'],
    ['展开尺寸', 'open'],
    ['envelope', 'envelope'],
    ['matching envelope', 'envelope'],
    ['envelop', 'envelope'],
    ['bijpassende envelop', 'envelope'],
    ['信封尺寸', 'envelope'],
  ]).get(label) ?? null;
}

export function canonicalDimensionValue(value) {
  let text = String(value ?? '').replace(/\u00a0/g, ' ').trim().toLocaleLowerCase().replace(/[×*]/g, 'x').replace(/\s+/g, ' ');
  if (/\bnot\s+included\b|\bniet\s+inbegrepen\b|无信封/.test(text)) return null;
  text = text.replace(/\bincluded\b\s*$/i, '').trim();
  const number = '(\\d+(?:[.,]\\d+)?)';
  const match = new RegExp(`^${number}\\s*x\\s*${number}(?:\\s*x\\s*${number})?\\s*(cm|inches|inch|in)$`, 'i').exec(text);
  if (!match) return null;
  const unit = /^in/i.test(match[4]) ? 'in' : 'cm';
  const values = match.slice(1, 4).filter(Boolean).map((entry) => Number.parseFloat(entry.replace(',', '.')));
  if (values.some((numberValue) => !Number.isFinite(numberValue))) return null;
  return `${values.map((number) => String(number)).join('×')}${unit}`;
}

export function pairsAreCompleteAndEqual(upperPairs, lowerPairs) {
  if (!Array.isArray(upperPairs) || !Array.isArray(lowerPairs) || lowerPairs.length !== 3) return false;
  const normalizePairs = (pairs, requireEveryPair) => {
    const map = new Map();
    for (const pair of pairs) {
      if (!pair || pair.invalid) return null;
      const label = canonicalDimensionLabel(pair?.label);
      const value = canonicalDimensionValue(pair?.value);
      if (!label) {
        if (requireEveryPair) return null;
        continue;
      }
      if (!value || map.has(label)) return null;
      map.set(label, value);
    }
    return map;
  };
  const upper = normalizePairs(upperPairs, false);
  const lower = normalizePairs(lowerPairs, true);
  if (!upper || !lower || upper.size !== lower.size) return false;
  return [...lower].every(([label, value]) => upper.get(label) === value);
}

export function shouldHideLowerDimensions({ hasPriorSizeHeading, upperPairs, lowerPairs }) {
  return hasPriorSizeHeading === true && pairsAreCompleteAndEqual(upperPairs, lowerPairs);
}

function isBefore(first, second) {
  return Boolean(first.compareDocumentPosition(second) & 4);
}

function extractTablePairs(table) {
  return [...table.querySelectorAll('tr')]
    .map((row) => {
      const cells = row.querySelectorAll('th, td');
      if (cells.length < 2) return null;
      return { label: cells[0].textContent, value: cells[cells.length - 1].textContent };
    })
    .filter(Boolean);
}

function extractLowerPairs(section) {
  return [...section.querySelectorAll('li')]
    .map((item) => {
      const strong = item.querySelector('strong');
      if (!strong) return { invalid: true };
      const copy = item.cloneNode(true);
      copy.querySelector('strong')?.remove();
      return { label: strong.textContent, value: copy.textContent };
    });
}

export function lowerStructureIsExact({ headingText, listItemCount, hasExtraContent, hasUnexpectedListDescendant = false }) {
  return isSizeDetailsHeading(headingText) && listItemCount === 3 && hasExtraContent === false && hasUnexpectedListDescendant === false;
}

function lowerSectionStructureIsExact(section) {
  if (section.querySelector('h3 *, strong *, li > :not(strong)')) return false;
  const elementChildren = [...section.children];
  const headings = elementChildren.filter((element) => element.matches('h3'));
  const lists = elementChildren.filter((element) => element.matches('ul'));
  const unsupportedElements = elementChildren.some((element) => !element.matches('h3, ul'));
  const nonWhitespaceText = [...section.childNodes].some((node) => node.nodeType === 3 && node.textContent.trim() !== '');
  const list = lists[0];
  const unsupportedListChildren = list ? [...list.childNodes].some((node) => {
    if (node.nodeType === 3) return node.textContent.trim() !== '';
    return node.nodeType === 1 && !node.matches('li');
  }) : true;
  const unexpectedListDescendant = list ? [...list.children].some((item) => !item.matches('li') || [...item.children].some((child) => !child.matches('strong'))) : true;
  return lowerStructureIsExact({
    headingText: headings[0]?.textContent,
    listItemCount: list?.children.length ?? 0,
    hasExtraContent: unsupportedElements || nonWhitespaceText || headings.length !== 1 || lists.length !== 1 || unsupportedListChildren,
    hasUnexpectedListDescendant: unexpectedListDescendant,
  });
}

function markUpperSizeHeading(heading, lowerSection) {
  const lowerHeading = lowerSection.querySelector('h3');
  if (!lowerHeading || typeof window.getComputedStyle !== 'function') return;
  const fontSize = window.getComputedStyle(lowerHeading).fontSize;
  const lineHeight = window.getComputedStyle(lowerHeading).lineHeight;
  if (!fontSize || !lineHeight) return;
  heading.dataset.cardcSizeDetailsHeading = 'true';
  heading.style.setProperty('--cardc-refinement-size-heading-font-size', fontSize);
  heading.style.setProperty('--cardc-refinement-size-heading-line-height', lineHeight);
}

function markUpperTableCells(table, lowerSection) {
  const lowerListText = lowerSection.querySelector('li') ?? lowerSection.querySelector('ul');
  if (!lowerListText || typeof window.getComputedStyle !== 'function') return;
  const computed = window.getComputedStyle(lowerListText);
  if (!computed.fontSize || !computed.lineHeight) return;
  for (const cell of table.querySelectorAll('th, td')) {
    cell.dataset.cardcSizeDetailsCell = 'true';
    cell.style.setProperty('--cardc-refinement-size-table-font-size', computed.fontSize);
    cell.style.setProperty('--cardc-refinement-size-table-line-height', computed.lineHeight);
  }
}

function findPriorSizeHeading(root, section) {
  return [...root.querySelectorAll('h2')]
    .filter((heading) => isSizeDetailsHeading(heading.textContent) && isBefore(heading, section))
    .at(-1) ?? null;
}

function findPriorTable(root, heading, section) {
  return [...root.querySelectorAll('table')]
    .filter((table) => isBefore(heading, table) && isBefore(table, section))
    .at(-1) ?? null;
}

export function refineDescription(root) {
  const sections = [...root.querySelectorAll(LOWER_SELECTOR)];
  for (const section of sections) {
    const heading = findPriorSizeHeading(root, section);
    const table = heading ? findPriorTable(root, heading, section) : null;
    const lowerStructureExact = lowerSectionStructureIsExact(section);
    if (heading && lowerStructureExact) markUpperSizeHeading(heading, section);
    const duplicate = lowerStructureExact && shouldHideLowerDimensions({
      hasPriorSizeHeading: Boolean(heading),
      upperPairs: table ? extractTablePairs(table) : [],
      lowerPairs: extractLowerPairs(section),
    });
    if (duplicate) {
      if (table) markUpperTableCells(table, section);
      section.hidden = true;
      section.dataset.cardcDimensionsDuplicateHidden = 'true';
    } else if (section.dataset.cardcDimensionsDuplicateHidden === 'true') {
      section.hidden = false;
      delete section.dataset.cardcDimensionsDuplicateHidden;
    }
  }
}

function descriptionRoots(documentRoot) {
  const candidates = [...documentRoot.querySelectorAll(DESCRIPTION_SELECTOR)]
    .filter((root) => !root.closest('.cardc-amazon-pdp'))
    .filter((root) => root.querySelector('h2, h3, table, .cardc-dimensions'));
  return candidates.filter((root) => !candidates.some((other) => other !== root && other.contains(root)));
}

export function addedNodesContainDescriptionRoot(addedNodes) {
  return [...addedNodes].some((node) => node?.nodeType === 1 && (
    node.matches?.(DESCRIPTION_SELECTOR) || node.querySelector?.(DESCRIPTION_SELECTOR)
  ));
}

function observeDescription(root) {
  if (observedRoots.has(root) || typeof MutationObserver === 'undefined') return;
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      refineDescription(root);
    });
  });
  observer.observe(root, { childList: true, subtree: true });
  observedRoots.add(root);
}

function observeMainContent(mainContent, documentRoot) {
  if (observedMainContents.has(mainContent) || typeof MutationObserver === 'undefined') return;
  let scheduled = false;
  const observer = new MutationObserver((records) => {
    const relevant = records.some((record) => addedNodesContainDescriptionRoot(record.addedNodes));
    if (!relevant || scheduled || !documentRoot.querySelector('#MainContent [data-cardc-halloween-pdp-refinements-loader]')) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      mountPdpRefinements(documentRoot);
    });
  });
  observer.observe(mainContent, { childList: true, subtree: true });
  observedMainContents.set(mainContent, observer);
}

export function mountPdpRefinements(documentRoot = document) {
  const mainContent = documentRoot.querySelector?.('#MainContent');
  if (!mainContent || !documentRoot.querySelector('#MainContent [data-cardc-halloween-pdp-refinements-loader]')) return;
  for (const root of descriptionRoots(documentRoot)) {
    refineDescription(root);
    observeDescription(root);
  }
  observeMainContent(mainContent, documentRoot);
}

if (typeof document !== 'undefined') {
  const mount = () => mountPdpRefinements(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
}
